/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';

export const MAX_GLOBAL_SEARCH_LIMIT = 20;

export const getSearchTitle = (title: string) => {
  if (title?.length > MAX_GLOBAL_SEARCH_LIMIT) {
    return title.substring(0, MAX_GLOBAL_SEARCH_LIMIT).concat('...');
  }
  return title;
};

export interface GlobalSearchTitleProps {
  searchKeyword?: string;
  searchModule?: string;
}

const useStyles = makeStyles(() => ({
  dialogTitle : {
    marginLeft    : '20px',
    whiteSspace   : 'nowrap',
    overflow      : 'hidden',
    textOverflow  : 'ellipsis',
    textTransform : 'none !important'
  }
}));

/**
 * Defines the GlobalSearchTitle component. This component is used to
 * display the title names with tooltip.
 *
 * ```typescript
 *
 *   import GlobalSearchTitle from './GlobalSearchTitle';
 *
 *   <GlobalSearchTitle searchKeyword={searchKeyword}
 *     searchModule={searchModule} />
 *
 * ```
 */
const GlobalSearchTitle = ({
  searchKeyword,
  searchModule
}: GlobalSearchTitleProps) => {
  const classes = useStyles();

  const dialogTitle = useMemo(() => {
    const searchTitle = getSearchTitle(searchKeyword);
    return translate('common:search.globalSearchResults', {text: searchTitle, module: searchModule});
  }, [searchKeyword, searchModule]);

  if (searchKeyword?.length > MAX_GLOBAL_SEARCH_LIMIT) {
    return (
      <Tooltip arrow
        title={<span>{searchKeyword}</span>}>
        <Typography className={classes.dialogTitle} component='h1' variant='h2'>
          {dialogTitle}
        </Typography>
      </Tooltip>
    );
  }
  return (
    <Typography className={classes.dialogTitle} component='h1' variant='h2'>
      {dialogTitle}
    </Typography>
  );
};

export default React.memo(GlobalSearchTitle);
